// src/components/Navigation.jsx - Yenile Butonu ile Tam Kod

import React, { useState } from 'react';
import { Table, Map, BarChart3, Bell, FileText, User, ChevronDown, Flame, Droplet, Layers } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Navigation = ({
    activeView,
    setActiveView,
    turfBoxRecordCount,
    folderDeviceCounts,
    notifications = []
}) => {
    const { t, language, changeLanguage } = useLanguage();
    const [showNotifications, setShowNotifications] = useState(false);
    const [showProfile, setShowProfile] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const locale = language === 'tr' ? 'tr-TR' : 'en-US';

    const navItems = [
        { id: 'dashboard', icon: BarChart3, label: t('nav.dashboard') },
        { id: 'map', icon: Map, label: t('nav.map') },
        { id: 'table', icon: Table, label: t('nav.data') },
        { id: 'units', icon: Layers, label: t('nav.units') },
        { id: 'irrigation', icon: Droplet, label: t('nav.irrigation') },
        { id: 'heating', icon: Flame, label: t('nav.heating') },
        { id: 'reports', icon: FileText, label: t('nav.reports') },
        { id: 'notifications', icon: Bell, label: t('nav.notifications') }
    ];

    const unreadCount = notifications.filter(n => n.unread).length;

    return (
        <>
            <nav className="bg-slate-100 shadow-lg sticky top-0 z-50 border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 py-3">
                    {/* Top Row */}
                    <div className="flex items-center justify-between mb-3">
                        {/* Başlık */}
                        <div className="flex items-center gap-4">
                            <h1 className="text-2xl font-bold text-slate-900">
                                <a
                                    href="https://www.proconsport.com/"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="hover:text-slate-700 transition-colors"
                                >
                                    {t('nav.title')}
                                </a>
                            </h1>
                            <div className="text-sm text-slate-600">
                                <p>{t('nav.infoTeam', { team: 'Kocaelispor' })}</p>
                                <p>{t('nav.infoField', { field: 'Kocaelispor Stadyumu' })}</p>
                            </div>
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setShowInfo(!showInfo);
                                        setShowNotifications(false);
                                        setShowProfile(false);
                                    }}
                                    className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-300 bg-white text-xs font-semibold text-slate-600 shadow-sm transition hover:border-slate-400 hover:text-slate-900"
                                >
                                    i
                                </button>

                                {showInfo && (
                                    <div className="absolute left-0 mt-2 w-64 rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700 shadow-xl z-50">
                                        <p className="mt-2 font-medium text-slate-900">
                                            {t('nav.infoRecordCount', { count: turfBoxRecordCount.toLocaleString(locale) })}
                                        </p>
                                        <p className="mt-1 text-slate-700">
                                            {t('nav.infoDeviceCount', { count: folderDeviceCounts.turfBox.toLocaleString(locale) })}
                                        </p>
                                        <p className="mt-1 text-slate-700">
                                            {t('nav.infoUnitCount')}{' '}
                                            {folderDeviceCounts.unite > 0
                                                ? folderDeviceCounts.unite.toLocaleString(locale)
                                                : t('nav.noData')}
                                        </p>
                                        <p className="mt-1 text-slate-700">
                                            {t('nav.infoIrrigationSystems')}{' '}
                                            {folderDeviceCounts.sulama > 0
                                                ? folderDeviceCounts.sulama.toLocaleString(locale)
                                                : t('nav.noData')}
                                        </p>
                                        <p className="mt-1 text-slate-700">
                                            {t('nav.infoHeatingSystems')}{' '}
                                            {folderDeviceCounts.isitma > 0
                                                ? folderDeviceCounts.isitma.toLocaleString(locale)
                                                : t('nav.noData')}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setShowProfile(!showProfile);
                                        setShowNotifications(false);
                                        setShowInfo(false);
                                    }}
                                    className="flex items-center gap-2 rounded-full bg-white/90 px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 hover:text-slate-900 hover:ring-slate-300 transition"
                                >
                                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-white">
                                        <User size={16} />
                                    </span>
                                    <span>{t('nav.profile')}</span>
                                    <ChevronDown size={14} className="text-slate-500" />
                                </button>

                                {showProfile && (
                                    <div className="absolute right-0 mt-3 w-52 rounded-xl bg-white shadow-xl border border-slate-200 z-50">
                                        <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                            {t('nav.language')}
                                        </div>
                                        <div className="flex flex-col pb-2">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    changeLanguage('en');
                                                    setShowProfile(false);
                                                }}
                                                className={`flex items-center justify-between px-4 py-2 text-sm transition ${language === 'en'
                                                    ? 'bg-slate-100 text-slate-900 font-semibold'
                                                    : 'text-slate-600 hover:bg-slate-50'
                                                    }`}
                                            >
                                                <span>{t('nav.english')}</span>
                                                {language === 'en' && (
                                                    <span className="text-xs text-slate-500">✓</span>
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    changeLanguage('tr');
                                                    setShowProfile(false);
                                                }}
                                                className={`flex items-center justify-between px-4 py-2 text-sm transition ${language === 'tr'
                                                    ? 'bg-slate-100 text-slate-900 font-semibold'
                                                    : 'text-slate-600 hover:bg-slate-50'
                                                    }`}
                                            >
                                                <span>{t('nav.turkish')}</span>
                                                {language === 'tr' && (
                                                    <span className="text-xs text-slate-500">✓</span>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setShowNotifications(!showNotifications);
                                        setShowProfile(false);
                                    }}
                                    className="relative flex items-center justify-center h-10 w-10 rounded-full bg-white/90 shadow-sm ring-1 ring-slate-200 text-slate-700 hover:text-slate-900 hover:ring-slate-300 transition"
                                >
                                    <Bell size={18} />
                                    {unreadCount > 0 && (
                                        <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full shadow">
                                            {unreadCount}
                                        </span>
                                    )}
                                </button>

                                {showNotifications && (
                                    <div className="absolute right-0 mt-3 w-80 rounded-xl bg-white shadow-xl border border-slate-200 z-50">
                                        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                                            <span className="text-sm font-semibold text-slate-900">
                                                {t('nav.notifications')}
                                            </span>
                                            <span className="text-xs text-slate-500">
                                                {unreadCount} {t('nav.unread')}
                                            </span>
                                        </div>
                                        <div className="max-h-72 overflow-y-auto">
                                            {notifications.map((notification) => (
                                                <div
                                                    key={notification.id}
                                                    className="px-4 py-3 border-b border-slate-100 last:border-b-0"
                                                >
                                                    <div className="flex items-start gap-3">
                                                        <span
                                                            className={`mt-1 h-2 w-2 rounded-full ${notification.unread ? 'bg-emerald-500' : 'bg-slate-300'}`}
                                                        />
                                                        <div className="flex-1">
                                                            <div className="flex items-center justify-between gap-2">
                                                                <span className={`text-xs uppercase tracking-wide ${notification.unread ? 'text-slate-600 font-semibold' : 'text-slate-400'}`}>
                                                                    {t(`notifications.types.${notification.type}`)}
                                                                </span>
                                                                <span className="text-xs text-slate-400">
                                                                    {notification.time}
                                                                </span>
                                                            </div>
                                                            <p className={`text-sm ${notification.unread ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>
                                                                {notification.message}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* ProconSport Logo */}
                            <a
                                href="https://www.proconsport.com/"
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center flex-shrink-0 rounded-md bg-white/90 px-3 py-2 shadow-sm ring-1 ring-slate-200 hover:ring-slate-300 transition"
                            >
                                <img
                                    src="/procon-logo.png"
                                    alt="ProconSport Logo"
                                    className="h-10"
                                    onError={(e) => {
                                        // Logo yüklenemezse gizle
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </a>
                        </div>
                    </div>

                    {/* Bottom Row */}
                    <div className="flex items-center justify-between gap-4">
                        {/* Navigation Buttons */}
                        <div className="flex gap-2">
                            {navItems.map((item) => {
                                const Icon = item.icon;
                                const isActive = activeView === item.id;

                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => setActiveView(item.id)}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all text-sm ${isActive
                                            ? 'bg-slate-900 text-white shadow-lg'
                                            : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-slate-900 hover:text-white'
                                            }`}
                                    >
                                        <Icon size={16} />
                                        {item.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </nav>

            {/* Overlay for dropdowns */}
            {(showNotifications || showProfile || showInfo) && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => {
                        setShowNotifications(false);
                        setShowProfile(false);
                        setShowInfo(false);
                    }}
                />
            )}
        </>
    );
};

export default Navigation;
